import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { FileOfDocument, GetUserAssetsOpts, PreSurveyAttachment, User, UserAsset } from '@interfaces';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserAssetsHttpService {
  private readonly http = inject(HttpClient);

  private API_URL = environment.API_URL;
  private FULL_URL = `${this.API_URL}/user-assets`;

  public uploadDocumentToReview(opts: { userProcessId: string; file: File }): Observable<UserAsset> {
    const url = `${this.FULL_URL}/my-assets/user-process/${opts.userProcessId}/to-review`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<UserAsset>(url, data);
  }

  public getMyDocumentsSentToReview(opts: { userProcessId: string }): Observable<UserAsset[]> {
    const url = `${this.FULL_URL}/my-assets/user-process/${opts.userProcessId}/in-review`;
    return this.http.get<UserAsset[]>(url);
  }

  public downloadMyAsset(opts: { userAssetId: string }): Observable<any> {
    const url = `${this.FULL_URL}/my-assets/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public removeMyAsset(opts: { userAssetId: string }): Observable<{ fileOriginalName: string; userAssetId: string }> {
    const url = `${this.FULL_URL}/my-assets/${opts.userAssetId}`;
    return this.http.delete<{ fileOriginalName: string; userAssetId: string }>(url);
  }

  public uploadDecisionDocument(opts: { userProcessId: string; file: File }): Observable<UserAsset> {
    const url = `${this.FULL_URL}/my-assets/user-process/${opts.userProcessId}/decision-document`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<UserAsset>(url, data);
  }

  public getDecisionDocuments(opts: { userProcessId: string }): Observable<UserAsset[]> {
    const url = `${this.FULL_URL}/my-assets/user-process/${opts.userProcessId}/decision-documents`;
    return this.http.get<UserAsset[]>(url);
  }

  public downloadDecisionDocument(opts: { userProcessId: string; userAssetId: string }): Observable<any> {
    const url = `${this.FULL_URL}/my-assets/user-process/${opts.userProcessId}/decision-document/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public uploadDecisionDocumentAsConsultant(opts: { userProcessId: string; file: File }): Observable<UserAsset> {
    const url = `${this.FULL_URL}/user-process/${opts.userProcessId}/decision-document`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<UserAsset>(url, data);
  }

  public getDecisionDocumentsAsConsultant(opts: { userProcessId: string }): Observable<UserAsset[]> {
    const url = `${this.FULL_URL}/user-process/${opts.userProcessId}/decision-documents`;
    return this.http.get<UserAsset[]>(url);
  }

  public downloadDecisionDocumentAsConsultant(opts: { userProcessId: string; userAssetId: string }): Observable<any> {
    const url = `${this.FULL_URL}/user-process/${opts.userProcessId}/decision-document/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public uploadTrackingFile(opts: { trackingId: string; file: File }): Observable<UserAsset> {
    const url = `${this.FULL_URL}/tracking/${opts.trackingId}`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<UserAsset>(url, data);
  }

  public downloadTrackingFile(opts: { trackingId: string; userAssetId: string }): Observable<any> {
    const url = `${this.FULL_URL}/tracking/${opts.trackingId}/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public removeTrackingFile(opts: { trackingId: string; userAssetId: string }): Observable<unknown> {
    const url = `${this.FULL_URL}/tracking/${opts.trackingId}/${opts.userAssetId}`;
    return this.http.delete(url);
  }

  private createDocumentFormData(file: File): FormData {
    const data = new FormData();
    const datetime = moment().format('DDMMYYYY_HHmmss_SSS');
    data.append(`${datetime}`, file, file.name);
    return data;
  }

  public uploadMessageAttachment(opts: { file: File }): Observable<UserAsset> {
    const url = `${this.FULL_URL}/my-assets/message-attachment`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<UserAsset>(url, data);
  }

  public getUserFilesOfDocuments(opts: GetUserAssetsOpts): Observable<FileOfDocument[]> {
    const { userId, ...payload } = opts;
    const url = `${this.API_URL}/user-documents/${userId}/documents-scans`;
    return this.http.post<FileOfDocument[]>(url, payload);
  }

  public getUserAssets(opts: GetUserAssetsOpts): Observable<UserAsset[]> {
    const url = `${this.API_URL}/support-chat/user/${opts.userId}/search-assets`;
    return this.http.post<UserAsset[]>(url, { ...opts });
  }

  public downloadAnyAsset(opts: { userAssetId: string }): Observable<any> {
    const url = `${this.FULL_URL}/any-asset/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public uploadPreSurveyAttachment(opts: {
    file: File;
    userProcessId: string;
    preSurveyId: string;
  }): Observable<PreSurveyAttachment> {
    const url = `${this.FULL_URL}/userProcess/${opts.userProcessId}/pre-survey/${opts.preSurveyId}/pre-survey-attachment`;
    const data = this.createDocumentFormData(opts.file);
    return this.http.post<PreSurveyAttachment>(url, data);
  }
}
